import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Writing a REST client in Haskell",
  "description": "",
  "published": "2013-04-25T00:00:00.000Z",
  "redirect_from": ["/blog/writing-a-rest-client-in-haskell/swizec/6152"],
  "categories": "Aeson, Haskell, JSON, Languages, Monads, Parsing, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A few days ago I decided to buy some bitcoin. Then I noticed it fluctuates a lot despite a general upwards trend. Hmmm ... if I just bought at the right moment and sold at a different right moment, I could make money fall out of the void!`}</p>
    <p>{`I have since lost $5 by playing and gained $30 by leaving it alone. Obviously I suck at this ...`}</p>
    <p>{`I know! Let's make a bot that does this! A low frequency trading bot, that sounds like fun. And let's write it in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{`, just to keep things interesting. Marrying the strictest of languages with the messiest of resources - the internet ... what could possibly go wrong?`}</p>
    <p>{`First order of business - a REST client.`}</p>
    <h2 {...{
      "id": "rest-client"
    }}>{`REST client`}</h2>
    <p>{`Before my bot can do any trading and intricate `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Algorithmic_trading",
        "title": "Algorithmic trading"
      }}>{`algorithmic trading`}</a>{`, it needs to talk to the marketplace of choice. I picked Bitstamp because they're the only ones that let me do this without a US bank account.`}</p>
    <p>{`Writing a REST client in most languages is simple. Reading Bitstamp's ticker in Python looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import requests, json

r = requests.get("https://www.bitstamp.net/api/ticker")
print json.loads(r.content)
# prints: {u'volume': u'17179.28558844', u'last': u'159.49', u'bid': u'159.49', u'high': u'161.00', u'low': u'139.00', u'ask': u'159.64'}
`}</code></pre>
    <p>{`That's it. Everything you need for a set of values nicely accessible as a dictionary.`}</p>
    <p>{`In Haskell, well in Haskell figuring out how to do that took me all night, then a bit of the morning and finally a helpful tweet from a stranger to tell me just how I was misusing monads.`}</p>
    <p>{`First of all, we are going to need a bunch of imports. The ones we `}<em parentName="p">{`really`}</em>{` care about are the `}<em parentName="p">{`http-conduit`}</em>{` library and the `}<em parentName="p">{`Aeson`}</em>{` parser of `}<a parentName="p" {...{
        "href": "http://json.org/",
        "title": "JSON"
      }}>{`JSON`}</a>{` strings. Everything else is there because ... well I'm not sure actually, but it seems to be necessary, otherwise things don't work.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`{-# LANGUAGE OverloadedStrings #-}

import Network.HTTP.Conduit
import Control.Monad.IO.Class
import Data.ByteString.Lazy
import Data.Aeson
import Data.Attoparsec.Number
import Control.Applicative
import Control.Monad.Trans
`}</code></pre>
    <p>{`I am not perfectly certain what the OverloadedStrings bit at the top does. It's some sort of compiler directive and most haskell libraries I find in the wild tell me I will be a much happier man if I turn it on. Shrug.`}</p>
    <p>{`All it takes now is making an `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Hypertext_Transfer_Protocol",
        "title": "Hypertext Transfer Protocol"
      }}>{`HTTP request`}</a>{` and parsing the response as JSON. Simple, right?`}</p>
    <p>{`Well, Haskell is strict and you can't just parse things all willy nilly. We need to tell the parser what we expect, what we want to do with the result and what `}<em parentName="p">{`type`}</em>{` it's going to be. Can't just have a bag of stuff! Nope, needs to be a well defined bag of things.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`data Ticker = Ticker
              { high :: Number,
                last :: Number,
                bid :: Number,
                volume :: Number,
                low :: Number,
                ask :: Number
              } deriving Show
`}</code></pre>
    <p>{`Great. We have a `}<em parentName="p">{`Ticker`}</em>{` type that has a bunch of numbers and some names. That `}{`_`}{`Show `}{`_`}{`part seems to say that we'll be able to print this out to the console. Smashing!`}</p>
    <p>{`That's not enough though, it is time for some strange hieroglyphics that tell `}<em parentName="p">{`Aeson`}</em>{` how exactly parsing works.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`instance FromJSON Ticker where
  parseJSON (Object v) = Ticker
                         v .: "high"
                         v .: "last"
                         v .: "bid"
                         v .: "volume"
                         v .: "low"
                         v .: "ask"
`}</code></pre>
    <p>{`If I understand this correctly, those strange symbols are applicatives. The `}<inlineCode parentName="p">{`.:`}</inlineCode>{` does ... something ... and the `}<inlineCode parentName="p">{`<$>`}</inlineCode>{` `}{`_`}{`and `}<inlineCode parentName="p">{`<*>`}</inlineCode>{` do something else. The whole bit is about defining how to convert a key in the JSON string into a value in the `}<inlineCode parentName="p">{`Ticker`}</inlineCode>{` type. I think.`}</p>
    <p>{`Right, let's make a function that will talk to the server and return a `}<inlineCode parentName="p">{`Ticker`}</inlineCode>{` object. Maybe. If all goes well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`ticker::(MonadIO m) => m (Maybe Ticker)
ticker = get "ticker" >>= return . decode
`}</code></pre>
    <p>{`Fairly simple stuff. Take something from the internet carefully wrapped in `}<em parentName="p">{`MonadIO`}</em>{`, unwrap it for a bit, feed it into `}<em parentName="p">{`decode`}</em>{`, which magically uses all the stuff we defined earlier, and wrap it back into both a `}<em parentName="p">{`MonadIO`}</em>{` and a `}<em parentName="p">{`Maybe.`}</em>{` Parsing can fail you know.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`get::(MonadIO m) => String -> m ByteString
get url = simpleHttp $ "https://www.bitstamp.net/api/"++url
`}</code></pre>
    <p>{`This is the generalized `}<em parentName="p">{`get`}</em>{` function that talks to Bitstamp using the `}<em parentName="p">{`simpleHttp`}</em>{` function from `}<em parentName="p">{`http-conduit`}</em>{`. It looks simple, but I'm sure a lot of hairy stuff is going on behind the scenes.`}</p>
    <p>{`To make sure everything works, we run it.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`main = do
  ticker >>= print
`}</code></pre>
    <p>{`Nothing.`}</p>
    <p>{`Yup, the output we get is Nothing. It is at this point you realize `}<em parentName="p">{`someone`}</em>{` isn't using JSON correctly and all those numbers are actually strings. Strings. Now how the hell do you tell Haskell to automagically transform those into Numbers before putting them in the Ticker object?`}</p>
    <p>{`Messy messy internet.`}</p>
    <p>{`But hey! Got Haskell to talk to a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Representational_state_transfer",
        "title": "Representational state transfer"
      }}>{`REST API`}</a>{`. How cool is that!?`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=5492ac2c-3abf-45a3-9680-dd8c31c07973",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      